import { useMutation, useQueryClient } from "@tanstack/react-query";
import ky from "ky";
import type {
  CreateAccountFormData,
  CreateAccountResponse,
  ServiceAccountCreationRequest
} from "@dealer-center/ddc-pages-account-search-interface";

const CREATE_ACCOUNT_QUERY_KEY = "ddc-pages-account-search";
const CREATE_ACCOUNT_ENDPOINT = "/landing/nxlambda/accountSearch/create";

// Map CreateAccountFormData to ServiceAccountCreationRequest
export const mapFormDataToServiceRequest = (
  formData: CreateAccountFormData
): ServiceAccountCreationRequest => {
  return {
    dealerCode: null,
    companyName: formData.companyName.trim(),
    program: null,
    programGroupType: null,
    status: "IN_PRODUCTION",
    type: formData.accountType,
    verticalType: formData.verticalType,
    franchiseNames: null,
    primaryAddress: {
      address1: formData.address1.trim() || null,
      address2: formData.address2?.trim() || null,
      city: formData.city?.trim() || null,
      state: formData.state?.trim() || null,
      postalCode: formData.zipCode?.trim() || null,
      country: formData.country?.trim() || null
    },
    requestedServiceAccountId:
      formData.requestedServiceAccountId?.trim().toLowerCase() || null,
    email: null,
    workPhone: null
  };
};

/**
 * Call the Lambda endpoint to create a new service account
 */
export const createAccount = async (
  request: ServiceAccountCreationRequest
): Promise<CreateAccountResponse> => {
  const response = await ky.post(CREATE_ACCOUNT_ENDPOINT, {
    json: request,
    throwHttpErrors: false
  });

  if (!response.ok) {
    const errorData = await response.json<{ message?: string }>();
    throw new Error(
      errorData.message ?? `Request failed with status code ${response.status}`
    );
  }

  return response.json<CreateAccountResponse>();
};

export const useCreateAccount = () => {
  const queryClient = useQueryClient();

  return useMutation<CreateAccountResponse, Error, CreateAccountFormData>({
    mutationFn: (formData: CreateAccountFormData) => {
      const serviceRequest = mapFormDataToServiceRequest(formData);
      return createAccount(serviceRequest);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [CREATE_ACCOUNT_QUERY_KEY]
      });
    },
    onError: error => {
      console.error("Create Account Error:", error);
    }
  });
};
